import joi from 'joi';
import React, { KeyboardEvent, ReactElement, useCallback, useMemo, useState } from 'react';
import { $updateTotals, UpdateTotals } from '@loyalty/shared-common/rule-api-client';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
  Theme,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { CurrencyAttributes } from '@loyalty/shared-common/gamification-api-client';
import {
  CurrencyBalanceAttributes,
  ProfileAttributes,
} from '@loyalty/shared-common/profile-api-client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { QlfNumberField, useAlerts } from '@qualifio/ui-commons';
import { QlfRte } from '@qualifio/ui-commons/dist/components/qlf-rte';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons/faArrowRight';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { CurrencyImage } from '../../../shared/currency/CurrencyImage';
import { formatNumber } from '../../../shared/utils';
import { useApi } from '../../../shared/api-client/ApiClientProvider';
import { useJoiResolver } from '../../../shared/forms/use-joi-resolver';
import { useProgram } from '../../../loyalty/ProgramContext';

const signValues = [
  { value: 1, text: '+' },
  { value: -1, text: '-' },
];

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  dialog: {
    minWidth: 500,
  },
  dialogTitle: {
    padding: spacing(4, 8, 1),
    textAlign: 'center',
  },
  dialogContent: {
    paddingTop: spacing(2),
    paddingBottom: spacing(1),
  },
  dialogActions: {
    padding: spacing(1, 8, 4),
    justifyContent: 'center',
  },
  amountSign: {
    fontWeight: 700,
  },
  currencyIcon: {
    marginLeft: spacing(0.5),
  },
  result: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'end',
    padding: spacing(1),
  },
  resultAmount: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'end',
    width: 105,
    paddingRight: 4,
    paddingLeft: spacing(0.5),
    fontWeight: 700,
  },
  commentLabel: {
    marginBottom: spacing(1),
  },
}));

export interface AdjustBalanceDialogProps {
  profile: ProfileAttributes;
  balance: CurrencyBalanceAttributes;
  currency: CurrencyAttributes | undefined;
  onClose: () => void;
  onApply?: () => Promise<void> | void;
}

interface FormValue {
  sign: number;
  rawAmount: string;
  comment: string;
  updateTotals: UpdateTotals;
}

const adjustBalanceFormSchema = joi.object<FormValue>({
  sign: joi.number().valid(-1, 1),
  rawAmount: joi.number().positive().integer().max(1000000),
  comment: joi.string(),
  updateTotals: $updateTotals,
});

export function AdjustBalanceDialog({
  profile,
  balance,
  currency,
  onClose,
  onApply,
}: AdjustBalanceDialogProps) {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const { notify } = useAlerts();
  const [applying, setApplying] = useState(false);
  const { program } = useProgram();

  const { eventProcessingApi } = useApi();

  if (!currency) {
    notify(
      t('profiles.currency-balances.adjust.apply.error', { error: 'Currency not found' }),
      'error',
    );
    onClose();
    return <></>;
  }

  const resolver = useJoiResolver(adjustBalanceFormSchema);
  const {
    register,
    watch,
    control,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValue>({
    defaultValues: {
      sign: 1,
      rawAmount: '',
      comment: '',
      updateTotals: {
        totalAccumulated: true,
        totalOverCurrencyPeriod: true,
        totalOverLevelPeriod: true,
      },
    },
    resolver,
  });

  const sign = watch('sign');
  const rawAmount = watch('rawAmount');
  const amount = useMemo(() => sign * (parseInt(rawAmount) || 0), [sign, rawAmount]);
  const formValue = watch();

  const onAmountKeyDown = useCallback(
    (event: KeyboardEvent<unknown>) => {
      if (event.key === '-') {
        setValue('sign', -1);
      }
      if (event.key === '+') {
        setValue('sign', 1);
      }
    },
    [setValue],
  );

  const handleApply = useCallback(async () => {
    setApplying(true);
    try {
      const comment = getValues('comment');
      await eventProcessingApi.adjustBalance(program.id, profile.id, {
        amount: amount,
        currencyId: currency.id,
        i18nAttributes: Object.fromEntries(
          program.languages.map(language => [language, { description: comment }]),
        ),
        updateTotals: formValue.updateTotals,
      });
      notify(
        t('profiles.currency-balances.adjust.apply.success', { member: profile.firstName }),
        'success',
      );
      await onApply?.();
      onClose();
    } catch (error) {
      console.error(error);
      notify(t('profiles.currency-balances.adjust.apply.error', { error }), 'error');
    } finally {
      setApplying(false);
    }
  }, [profile.id, program, currency.id, amount, formValue]);

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <div>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 0,
          }}
        >
          <FontAwesomeIcon icon={faTimes} />
        </IconButton>
        <DialogTitle className={classes.dialogTitle}>
          {t('profiles.currency-balances.adjust.title')}
        </DialogTitle>
        <form onSubmit={handleSubmit(handleApply, console.error)}>
          <DialogContent className={classes.dialogContent}>
            <Box
              sx={{
                margin: '0 auto',
                width: 'fit-content',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      flexGrow: 1,
                      mr: 1,
                    }}
                  >
                    {t('profiles.currency-balances.adjust.adjustment.label', {
                      member: profile.firstName,
                    })}
                    <Box
                      component="span"
                      sx={{
                        paddingLeft: 0.5,
                        fontWeight: 700,
                      }}
                    >
                      {formatNumber(balance.value || 0, i18n.language)}
                    </Box>
                    <CurrencyImage currency={currency} className={classes.currencyIcon} />
                  </Box>
                  <Controller
                    control={control}
                    name="sign"
                    render={({ field: { ref, ...field } }) => (
                      <TextField
                        {...field}
                        inputRef={ref}
                        select
                        SelectProps={{
                          classes: {
                            select: classes.amountSign,
                          },
                        }}
                        fullWidth={false}
                      >
                        {signValues.map(val => (
                          <MenuItem key={val.value} value={val.value}>
                            {val.text}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Box>
                <Controller
                  control={control}
                  name="rawAmount"
                  render={({ field: { ref, ...field }, fieldState: { error } }) => (
                    <QlfNumberField
                      {...field}
                      inputRef={ref}
                      sx={{
                        marginLeft: 1,
                        marginBottom: 0,
                        width: 105,
                        '& .MuiInputBase-input': {
                          textAlign: 'right',
                        },
                      }}
                      fullWidth={false}
                      onKeyDown={onAmountKeyDown}
                      autoComplete="off"
                      InputProps={{
                        endAdornment: (
                          <CurrencyImage currency={currency} className={classes.currencyIcon} />
                        ),
                      }}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Box>

              <div className={classes.result}>
                {t('profiles.currency-balances.adjust.result.label')}
                <div className={classes.resultAmount}>
                  {formatNumber(balance.value + amount, i18n.language)}
                  <CurrencyImage currency={currency} className={classes.currencyIcon} />
                </div>
              </div>
            </Box>

            <div className={classes.commentLabel}>
              {t('profiles.currency-balances.adjust.comment.label')}
            </div>
            <Controller
              control={control}
              name="comment"
              render={({ field: { ref, ...field }, fieldState: { error } }) => {
                const member = profile.firstName || t('profile.this-member');
                return (
                  <QlfRte
                    {...field}
                    inputRef={ref}
                    multiline
                    fullWidth
                    rows={3}
                    label={t('profiles.currency-balances.adjust.comment.placeholder', {
                      member,
                    })}
                    id="comment-textarea-id"
                    error={!!error}
                    helperText={error?.message}
                  />
                );
              }}
            />

            <CounterCorrection
              diff={amount}
              amountBefore={balance.totalAccumulated}
              enabled={!!formValue.updateTotals?.totalAccumulated}
              left={
                <Controller
                  control={control}
                  name="updateTotals.totalAccumulated"
                  render={({ field: { ref, value, ...field }, fieldState }) => (
                    <FormControlLabel
                      control={<Checkbox checked={value} inputRef={ref} {...field} />}
                      label={t('forms.adjust-balance.total-accumulated')}
                    />
                  )}
                />
              }
            />

            <CounterCorrection
              diff={amount}
              amountBefore={balance.totalOverCurrencyPeriod}
              enabled={!!formValue.updateTotals?.totalOverCurrencyPeriod}
              left={
                <Controller
                  control={control}
                  name="updateTotals.totalOverCurrencyPeriod"
                  render={({ field: { ref, value, ...field }, fieldState }) => (
                    <FormControlLabel
                      control={<Checkbox checked={value} inputRef={ref} {...field} />}
                      label={t('forms.adjust-balance.total-over-currency-period')}
                    />
                  )}
                />
              }
            />

            <CounterCorrection
              diff={amount}
              amountBefore={balance.totalOverLevelPeriod}
              enabled={!!formValue.updateTotals?.totalOverLevelPeriod}
              left={
                <Controller
                  control={control}
                  name="updateTotals.totalOverLevelPeriod"
                  render={({ field: { ref, value, ...field }, fieldState }) => (
                    <FormControlLabel
                      control={<Checkbox checked={value} inputRef={ref} {...field} />}
                      label={t('forms.adjust-balance.total-over-level-period')}
                    />
                  )}
                />
              }
            />
          </DialogContent>

          <DialogActions className={classes.dialogActions}>
            <Button color="grey" onClick={onClose}>
              {t('generic.actions.cancel')}
            </Button>
            <Button
              color="success"
              type="submit"
              startIcon={applying && <FontAwesomeIcon icon={faSpinner} spin />}
              disabled={applying}
              autoFocus
            >
              {t('profiles.currency-balances.adjust.apply.label')}
            </Button>
          </DialogActions>
        </form>
      </div>
    </Dialog>
  );
}

function CounterCorrection({
  left,
  amountBefore,
  diff,
  enabled,
}: {
  left: ReactElement;
  amountBefore: number | undefined;
  diff: number | undefined;
  enabled: boolean;
}) {
  const previousValue = amountBefore || 0;
  const newValue = previousValue + (diff || 0);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      {left}
      <Box>
        {previousValue} <FontAwesomeIcon icon={faArrowRight} />
        {enabled ? newValue : previousValue}
      </Box>
    </Box>
  );
}
